import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, CreditCard, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

const cycleStatusColors = {
  active: { bg: "#D1FAE5", text: "#059669" },
  closed: { bg: "#F5F3EF", text: "#57534E" },
  invoiced: { bg: "#DBEAFE", text: "#1D4ED8" },
};

export default function Billing() {
  const navigate = useNavigate();
  const [cycles, setCycles] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [filterClient, setFilterClient] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [form, setForm] = useState({ client_id: "", start_date: "", end_date: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([api.get("/billing/cycles"), api.get("/clients")]).then(([cy, cl]) => {
      setCycles(cy.data);
      setClients(cl.data);
    }).finally(() => setLoading(false));
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const r = await api.post("/billing/cycles", form);
      setCycles((prev) => [r.data, ...prev]);
      setShowDialog(false);
      setForm({ client_id: "", start_date: "", end_date: "" });
      toast.success("Billing cycle created");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  const handleClose = async (cycleId) => {
    try {
      await api.post(`/billing/cycles/${cycleId}/close`);
      const r = await api.get("/billing/cycles");
      setCycles(r.data);
      toast.success("Cycle closed & next cycle created");
    } catch {
      toast.error("Failed to close cycle");
    }
  };

  const filtered = cycles.filter((c) => {
    const matchClient = !filterClient || c.client_id === filterClient;
    const matchStatus = !filterStatus || c.status === filterStatus;
    return matchClient && matchStatus;
  });

  return (
    <Layout>
      <div className="p-4 sm:p-6 lg:p-8" data-testid="billing-page">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-serif text-3xl font-bold" style={{ color: "#292524" }}>Billing</h1>
            <p className="text-sm mt-0.5" style={{ color: "#57534E" }}>Manage billing cycles per client</p>
          </div>
          <Button
            data-testid="add-billing-cycle-button"
            onClick={() => setShowDialog(true)}
            className="flex items-center gap-2"
            style={{ background: "#292524", color: "#fff" }}
          >
            <Plus size={15} strokeWidth={1.5} />
            New Cycle
          </Button>
        </div>

        {/* Filters */}
        <div className="flex gap-3 mb-6">
          <select
            value={filterClient}
            onChange={(e) => setFilterClient(e.target.value)}
            className="px-3 py-2 rounded-lg border text-sm"
            style={{ borderColor: "#E7E5E4", background: "#fff", color: "#292524" }}
          >
            <option value="">All Clients</option>
            {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-3 py-2 rounded-lg border text-sm"
            style={{ borderColor: "#E7E5E4", background: "#fff", color: "#292524" }}
          >
            <option value="">All Statuses</option>
            <option value="active">Active</option>
            <option value="closed">Closed</option>
            <option value="invoiced">Invoiced</option>
          </select>
        </div>

        <div className="space-y-3">
          {loading ? (
            <div className="text-center py-12 text-sm" style={{ color: "#A8A29E" }}>Loading...</div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12">
              <CreditCard size={32} className="mx-auto mb-3" style={{ color: "#E7E5E4" }} />
              <p className="text-sm" style={{ color: "#A8A29E" }}>No billing cycles found</p>
            </div>
          ) : (
            filtered.map((cyc) => {
              const sc = cycleStatusColors[cyc.status] || { bg: "#F5F3EF", text: "#57534E" };
              const additionalHours = cyc.additional_hours || 0;
              const client = clients.find((c) => c.id === cyc.client_id);
              const hourlyRate = client?.billing_config?.hourly_rate || 0;
              const estAmount = cyc.total_hours_logged > 0 ? cyc.total_hours_logged * hourlyRate : 0;
              return (
                <div
                  key={cyc.id}
                  data-testid={`billing-cycle-${cyc.id}`}
                  onClick={() => navigate(`/clients/${cyc.client_id}?tab=billing`)}
                  className="bg-white rounded-xl border p-5 cursor-pointer hover:shadow-md transition-shadow"
                  style={{ borderColor: "#E7E5E4" }}
                >
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <p className="font-medium" style={{ color: "#292524" }}>{cyc.client_name}</p>
                      <p className="text-sm" style={{ color: "#A8A29E" }}>
                        {cyc.start_date?.slice(0, 10)} — {cyc.end_date?.slice(0, 10)}
                      </p>
                    </div>
                    <div className="flex items-center gap-3" onClick={(e) => e.stopPropagation()}>
                      <span className="text-xs font-medium px-2.5 py-1 rounded-full" style={{ background: sc.bg, color: sc.text }}>
                        {cyc.status}
                      </span>
                      {cyc.status === "active" && (
                        <Button size="sm" variant="outline" onClick={() => handleClose(cyc.id)} data-testid={`close-cycle-${cyc.id}`}>
                          Close Cycle
                        </Button>
                      )}
                      {cyc.status === "closed" && (
                        <Button size="sm" onClick={() => navigate(`/invoices?cycle=${cyc.id}`)} style={{ background: "#292524", color: "#fff" }}>
                          Generate Invoice
                        </Button>
                      )}
                    </div>
                  </div>
                  <div className="grid grid-cols-4 gap-4">
                    <div>
                      <p className="text-xs" style={{ color: "#A8A29E" }}>Retained</p>
                      <p className="font-semibold text-lg font-serif" style={{ color: "#292524" }}>{cyc.retained_hours}h</p>
                    </div>
                    <div>
                      <p className="text-xs" style={{ color: "#A8A29E" }}>Logged</p>
                      <p className="font-semibold text-lg font-serif" style={{ color: "#292524" }}>{(cyc.total_hours_logged || 0).toFixed(1)}h</p>
                    </div>
                    <div>
                      <p className="text-xs" style={{ color: "#A8A29E" }}>Additional</p>
                      <p className="font-semibold text-lg font-serif" style={{ color: additionalHours > 0 ? "#DC2626" : "#292524" }}>
                        {additionalHours > 0 ? `+${additionalHours.toFixed(1)}h` : "0h"}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs" style={{ color: "#A8A29E" }}>Est. Amount</p>
                      <p className="font-semibold text-lg font-serif" style={{ color: "#292524" }}>
                        {hourlyRate > 0 ? `£${estAmount.toFixed(0)}` : "—"}
                      </p>
                    </div>
                  </div>
                  {cyc.retained_hours > 0 && cyc.total_hours_logged > 0 && (
                    <div className="mt-4">
                      <div className="flex justify-between text-xs mb-1" style={{ color: "#A8A29E" }}>
                        <span>Hours used vs retained</span>
                        <span>{Math.round(Math.min(cyc.total_hours_logged, cyc.retained_hours) / cyc.retained_hours * 100)}%</span>
                      </div>
                      <div className="h-1.5 rounded-full overflow-hidden" style={{ background: "#E7E5E4" }}>
                        <div className="h-full rounded-full" style={{ width: `${Math.min(100, cyc.total_hours_logged / cyc.retained_hours * 100)}%`, background: additionalHours > 0 ? "#DC2626" : "#D97706" }} />
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent data-testid="create-billing-cycle-dialog">
          <DialogHeader><DialogTitle className="font-serif">New Billing Cycle</DialogTitle></DialogHeader>
          <form onSubmit={handleCreate} className="space-y-3">
            <div>
              <Label>Client *</Label>
              <select required value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })}
                className="w-full px-3 py-2 rounded-lg border text-sm" style={{ borderColor: "#E7E5E4", background: "#fff" }}>
                <option value="">Select client</option>
                {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Start Date *</Label><Input type="date" required value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></div>
              <div><Label>End Date *</Label><Input type="date" required value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} /></div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
              <Button type="submit" disabled={saving} style={{ background: "#292524", color: "#fff" }}>{saving ? "Creating..." : "Create"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
